import { RefObject, useEffect } from 'react';

/**
 * Keep a fixed body-portal menu glued to its trigger, and close it the
 * moment the trigger can't be seen.
 *
 * A menu portaled to document.body holds its viewport spot while the
 * trigger scrolls away with its card — inside the item modal's own scroller
 * as much as the page. Re-measuring on scroll keeps it attached, but
 * following alone isn't enough: on the Tended page the trigger slides UNDER
 * the sticky channel/page chrome, and a menu that keeps following paints on
 * top of that chrome. So on every scroll/resize while open:
 *
 *   trigger off-screen, or something else paints over it → close
 *   trigger visible                                        → re-place
 *
 * "Something else" is read from the real hit-test stack
 * (`elementsFromPoint`), skipping the menu's own portal (a click-catcher
 * backdrop covers the trigger by design). Inside the item modal nothing
 * occludes the trigger — the modal is above all page chrome — so the menu
 * follows freely; on a plain page the chrome wins by closing, never by
 * z-index arithmetic.
 *
 * Capture-phase scroll catches nested scrollers (the modal), not just the
 * window.
 */
export function useFollowAnchor(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  place: () => void,
  close: () => void,
  /** The menu's own portal root — ignored when reading what covers the trigger. */
  ownPortalRef?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;
    const check = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      if (r.width === 0 || cy < 0 || cy > window.innerHeight || cx < 0 || cx > window.innerWidth) {
        close();
        return;
      }
      if (typeof document.elementsFromPoint === 'function') {
        const own = ownPortalRef?.current;
        const top = document.elementsFromPoint(cx, cy).find((n) => !(own && own.contains(n)));
        if (top && !anchor.contains(top)) {
          close();
          return;
        }
      }
      place();
    };
    window.addEventListener('scroll', check, true);
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check, true);
      window.removeEventListener('resize', check);
    };
  }, [open, anchorRef, place, close, ownPortalRef]);
}

export default useFollowAnchor;
